/**
 * E01 · THE PORTAL'S THOUGHT TEST, AS EIGHT FACTS (board chapter "Therapist Records a Thought
 * (portal)", founder 2026-09-11: *"I want full thing, including unfinished thought tests"*).
 *
 * The chapter has modelled these slices since July — `RecordActivatingEvent` through `RateOutcome` —
 * and only `CreatePortalCard` was ever built. So a therapist filling in the full form for a
 * converted client wrote a legacy row and the log learned nothing: `thoughts/17620` in production,
 * with no `thoughtTestId` and no events, for a client who has been `mode: es` since 2026-08-31.
 *
 * ─── WHY A BATCH AND NOT ONE COMMAND ───────────────────────────────────────────────────────────
 * The portal form is ONE page the therapist fills and saves once, where mobile is a wizard that
 * flushes per step. Both end at the same transport: `submitThoughtTestEvents` takes a list of
 * proposals for ONE boundary and does a single conditional append, so a page-at-a-time save is just
 * a batch with every step in it. The order is the wizard's order, because the cumulative decide
 * reads `clientSeq` and a later step may depend on an earlier one having landed.
 *
 * ─── WHAT THE SERVER OWNS, AND THIS FILE MUST NOT ──────────────────────────────────────────────
 *  • `performer` — stamped from the authenticated caller. A payload that could name it could name
 *    anyone, so it is deliberately absent here.
 *  • `legacyActivatingEventId` / the `thoughts` and `activatingEvents` rows — resolved and written
 *    by `wizardBindings` in the same transaction (T114 (d)).
 *  • `occurredAt` — one instant for the whole batch: the save is a single act.
 *
 * ─── WHAT IT DOES OWN ──────────────────────────────────────────────────────────────────────────
 * The two identities. `thoughtTestId` is the DCB boundary and `activatingEventId` the situation the
 * run shares; both are minted HERE so a retry of the same save is idempotent rather than a second
 * record — the same reason `createPortalCard` mints its own.
 *
 * A STEP WITH NOTHING IN IT IS NOT RECORDED. An empty evidence list is not the same fact as "the
 * therapist looked and found none", and `UnfinishedThoughtTests` reads exactly which steps have
 * landed to decide where to resume — so writing empty steps would mark a half-filled test complete.
 * Cognitive distortions are the deliberate exception: the board makes an empty selection a recorded
 * fact, so it is sent whenever the therapist reached that step.
 */

export type EsThoughtTestValues = {
  description?: string;
  emotions?: unknown[];
  feelings?: number[];
  bodyPartsFeelings?: number;
  behaviors?: string;
  automaticThought?: string;
  thoughtBelief?: number;
  cardName?: string;
  thoughtsFor?: { for?: string; alternativeThought?: string | null }[];
  thoughtsAgainst?: string[];
  cognitiveDistortions?: (string | number)[];
  realisticThoughts?: { thought?: string; thoughtBelief?: number }[];
  result?: {
    initialThoughtBelief?: number;
    /** the legacy row's spelling, sic — the event's is corrected (D-fhir) */
    initialNegativeEmotionStrenght?: number;
  };
};

export type ThoughtTestProposal = {
  eventId: string;
  type: string;
  occurredAt: string;
  clientSeq: number;
  payload: Record<string, unknown>;
};

export type EsThoughtTestBatch = {
  thoughtTestId: string;
  activatingEventId: string;
  events: ThoughtTestProposal[];
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const filled = (xs: (string | null | undefined)[] | undefined) =>
  (xs ?? []).filter((x) => !!x && String(x).trim().length > 0);

export const esThoughtTestBatch = (input: {
  subject: string;
  values: EsThoughtTestValues;
  /** Supplied when RE-firing steps onto a test that already exists; minted otherwise. */
  thoughtTestId?: string;
  activatingEventId?: string;
  /**
   * Did the therapist actually RATE the outcome?
   *
   * It cannot be read off the values: the form seeds `result` with 50/50, so a test nobody reached
   * the end of looks identical to one rated exactly in the middle. `OutcomeRated` is the event that
   * COMPLETES a test — `UnfinishedThoughtTests` stops listing a boundary once it lands — so taking
   * the seeded default for an answer marks every portal save finished and makes the unfinished list
   * permanently empty. The form knows the difference (antd tracks touched fields); this asks it.
   */
  outcomeRated?: boolean;
  /**
   * RESUME MODE: the top-level form fields the therapist actually TOUCHED this time.
   *
   * Re-firing a step SUPERSEDES it (founder 2026-07-26), so a resume that re-sent every step would
   * overwrite answers with whatever the form happened to hold. That is not theoretical: the draft
   * read carries the situation's DESCRIPTION but not its emotions, feelings or body map — those
   * live on the activating event — so re-firing `ActivatingEventRecorded` unchanged would erase
   * what the person originally recorded about how the moment felt.
   *
   * Given this list, a step is written only when the therapist put a hand on it. Absent (a NEW
   * test) every filled step is written, which is what a first save means.
   */
  touchedFields?: string[];
  now?: Date;
}): EsThoughtTestBatch => {
  const { subject, values } = input;
  /** In resume mode, did the therapist touch any field this step is built from? */
  const touched = (...fields: string[]) =>
    !input.touchedFields ||
    fields.some((f) => input.touchedFields!.includes(f));
  const thoughtTestId = input.thoughtTestId ?? `thoughtTests/${newId()}`;
  const activatingEventId = input.activatingEventId ?? `situations/${newId()}`;
  const occurredAt = (input.now ?? new Date()).toISOString();

  const base = { subject, thoughtTestId };
  const drafts: { type: string; payload: Record<string, unknown> }[] = [];

  // 1 — the situation. Recorded when it has ANY content: a description, a feeling, an emotion.
  const emotions = values.emotions ?? [];
  const feelings = values.feelings ?? [];
  const bodyPartsFeelings = values.bodyPartsFeelings ?? 0;
  /**
   * THE SITUATION IS EDITABLE ON A RESUME AGAIN, because the form can now SEE all of it.
   *
   * `ActivatingEventRecorded` carries the whole situation — description, emotions, feelings and the
   * body map — and re-firing supersedes all four together. While `ThoughtTestDraft` returned only
   * the description (FIELD-5b), a therapist correcting a typo would have written `emotions: []` over
   * what the person recorded, so this step was held read-only on a resume. The draft now carries
   * every field and the form is pre-filled from it, so a re-fire rewrites what is really there.
   */
  if (
    touched('description', 'emotions', 'feelings', 'bodyPartsFeelings') &&
    ((values.description ?? '').trim() ||
      emotions.length ||
      feelings.length ||
      bodyPartsFeelings)
  ) {
    drafts.push({
      type: 'ActivatingEventRecorded',
      payload: {
        ...base,
        activatingEventId,
        description: values.description ?? '',
        emotions,
        feelings,
        bodyPartsFeelings,
      },
    });
  }

  // 2 — behaviours. Its own step, and free text the person may legitimately leave blank; only the
  // blank-AND-untouched case is silent, which is the same thing here.
  if (touched('behaviors') && (values.behaviors ?? '').trim()) {
    drafts.push({
      type: 'BehaviorsRecorded',
      payload: { ...base, activatingEventId, behaviors: values.behaviors },
    });
  }

  // 3 — the automatic thought. The step the whole test hangs off.
  if (
    touched('automaticThought', 'thoughtBelief', 'cardName') &&
    (values.automaticThought ?? '').trim()
  ) {
    drafts.push({
      type: 'AutomaticThoughtRecorded',
      payload: {
        ...base,
        activatingEventId,
        automaticThought: values.automaticThought,
        thoughtBelief: values.thoughtBelief ?? 50,
        cardName: values.cardName ?? '',
      },
    });
  }

  // 4 — evidence FOR, with its per-line alternative explanations kept index-aligned
  const forRows = (values.thoughtsFor ?? []).filter((r) =>
    (r?.for ?? '').trim(),
  );
  if (touched('thoughtsFor') && forRows.length) {
    drafts.push({
      type: 'EvidenceForRecorded',
      payload: {
        ...base,
        thoughtsFor: forRows.map((r) => r.for),
        thoughtsForAlternativeThoughts: forRows.map(
          (r) => r.alternativeThought ?? null,
        ),
      },
    });
  }

  // 5 — evidence AGAINST
  const against = filled(values.thoughtsAgainst);
  if (touched('thoughtsAgainst') && against.length) {
    drafts.push({
      type: 'EvidenceAgainstRecorded',
      payload: { ...base, thoughtsAgainst: against },
    });
  }

  // 6 — cognitive distortions. OPTIONAL by founder ruling (2026-07-26) and an empty selection is a
  // RECORDED fact — "I looked and found none" — so `[]` is sent, not skipped, once the field exists.
  if (
    touched('cognitiveDistortions') &&
    values.cognitiveDistortions !== undefined
  ) {
    drafts.push({
      type: 'CognitiveDistortionsIdentified',
      payload: {
        ...base,
        // catalogue CODES on the wire ([String!]), whatever the form holds them as
        cognitiveDistortions: (values.cognitiveDistortions ?? []).map(String),
      },
    });
  }

  // 7 — the alternative thought: the card's back, one entry per line
  const realistic = (values.realisticThoughts ?? []).filter((r) =>
    (r?.thought ?? '').trim(),
  );
  if (touched('realisticThoughts') && realistic.length) {
    drafts.push({
      type: 'AlternativeThoughtRecorded',
      payload: {
        ...base,
        realisticThoughts: realistic.map((r) => ({
          thought: r.thought,
          thoughtBelief: r.thoughtBelief ?? 50,
        })),
      },
    });
  }

  // 8 — the outcome. THE EVENT THAT COMPLETES THE TEST: `UnfinishedThoughtTests` stops listing a
  // boundary once this lands, so it is sent only when the therapist actually rated it. Note the
  // spelling: the legacy row says `Strenght`, the event says `Strength` (D-fhir).
  const result = values.result;
  if (input.outcomeRated && result) {
    drafts.push({
      type: 'OutcomeRated',
      payload: {
        ...base,
        result: {
          initialThoughtBelief: result.initialThoughtBelief ?? 50,
          initialNegativeEmotionStrength:
            result.initialNegativeEmotionStrenght ?? 50,
        },
      },
    });
  }

  return {
    thoughtTestId,
    activatingEventId,
    events: drafts.map((d, index) => ({
      eventId: newId(),
      type: d.type,
      occurredAt,
      clientSeq: index, // the cumulative decide reads them in this order
      payload: d.payload,
    })),
  };
};
